<template>
    <o-modal :modalClass="'modal-xl'" @close="closeModal">
        <template v-slot:header>
            <h5 class="mb-0 font-weight-bold"><i class="fas fa-clock primary-text me-2"></i> Pending Points</h5>
        </template>
        <template v-slot:body>
            <!-- Search Control (Tabs + Input) -->
            <div class="search-box-custom mb-3">
                <div class="search-type-tabs">
                    <button
                        v-for="option in searchOptions"
                        :key="option.value"
                        class="tab-btn"
                        :class="{ active: searchBy.selected === option.value }"
                        @click="selectSearchType(option.value)"
                    >
                        {{ option.text }}
                    </button>
                </div>
                <div class="search-input-wrapper" v-if="searchBy.selected !== 'date_range'">
                    <input
                        type="text"
                        class="form-control-search"
                        v-model="searchBy.criteria"
                        :placeholder="searchPlaceholder"
                        @keyup.enter="applyFilter()"
                    />
                    <button v-if="!isSearchActive" class="btn-search" @click="applyFilter()">
                        <i class="fas fa-search"></i>
                    </button>
                    <button v-else class="btn-search" @click="clearFilter()">
                        <i class="fas fa-times text-danger"></i>
                    </button>
                </div>
                <div class="search-input-wrapper" v-else>
                    <div class="date-input-group">
                        <DatePicker v-model="dateStart" :max="dateEnd" placeholder="Start Date" />
                        <span class="date-separator">-</span>
                        <DatePicker v-model="dateEnd" :min="dateStart" placeholder="End Date" />
                    </div>
                    <button class="btn-search" @click="applyFilter()">
                        <i class="fas fa-filter"></i>
                    </button>
                    <button v-if="isSearchActive" class="btn-search" @click="clearFilter()">
                        <i class="fas fa-times text-danger"></i>
                    </button>
                </div>
            </div>

            <!-- Bulk Materialize Button -->
            <div v-if="selectedIds.length > 0" class="d-flex justify-content-between align-items-center mb-3">
                <span class="text-muted">
                    {{ selectedIds.length }} transaction's selected
                    <span v-if="isAllSelected" class="badge-all-selected">All pages</span>
                    <span v-else class="badge-page-selected">This page</span>
                </span>
                <button class="btn btn-sm btn-materialize-bulk" @click="handleMaterializeBulk()" :disabled="isMaterializing">
                    <i class="fas fa-check-double me-1"></i> Materialize Selected ({{ selectedIds.length }})
                </button>
            </div>

            <div class="d-flex justify-content-center py-5" v-if="page.isLoading">
                <div class="spinner-border primary-text" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>

            <div v-if="!page.isLoading && page.output">
                <o-table style="text-align:left;" class="table-hover pending-list-table">
                    <thead>
                    <tr>
                        <th class="fw-bolder text-muted select-all-th" scope="col">
                            <div class="select-all-wrapper">
                                <input type="checkbox" class="form-check-input" v-model="selectAll" @change="toggleSelectAll('toggle')" title="Select All" />
                                <div class="select-all-dropdown">
                                    <button type="button" class="select-all-caret" @click.stop="showSelectMenu = !showSelectMenu" title="Select All Options">
                                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                                            <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <div v-if="showSelectMenu" class="select-all-menu">
                                        <button type="button" @click="toggleSelectAll('page')">
                                            <i class="fas fa-file-alt me-2"></i> Select This Page
                                        </button>
                                        <button type="button" @click="toggleSelectAll('all')">
                                            <i class="fas fa-layer-group me-2"></i> Select All ({{ totalDataCount }} data)
                                        </button>
                                        <button type="button" @click="clearSelection()" v-if="selectedIds.length > 0">
                                            <i class="fas fa-times me-2"></i> Clear Selection
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </th>
                        <th class="fw-bolder text-muted" scope="col" v-for="(field, index) in fields" :key="index">
                            {{ field }}
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(item, index) in pendingTransactions" :key="item.id" class="member-row-clickable">
                        <td>
                            <input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds" />
                        </td>
                        <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                        <td><span class="code-badge">{{ item.code }}</span></td>
                        <td>{{ getMemberName(item) }}</td>
                        <td class="text-end">{{ formatNumber(item.point) }}</td>
                        <td>{{ formatDate(item.created_at) }}</td>
                        <td>
                            <button
                                @click="handleMaterializeSingle(item.id)"
                                class="btn btn-action btn-materialize"
                                :disabled="isMaterializing"
                            >
                                <i class="fas fa-check me-1"></i> Materialize
                            </button>
                        </td>
                    </tr>
                    <tr v-if="pendingTransactions.length === 0">
                        <td :colspan="fields.length + 1" class="text-center py-4">No pending transactions</td>
                    </tr>
                    </tbody>
                </o-table>
                <div class="container mt-4">
                    <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
                </div>
            </div>
        </template>
    </o-modal>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, reactive, ref } from 'vue';
import store from '@/store/';
import { OTable, OPagination, OModal } from '@/components/OmnihControl';
import DatePicker from '@/components/DatePicker.vue';
import { useToast } from '@/composables/useToast';

export default {
    props: {
        merchantId: {
            type: String,
            default: null,
        },
        branchId: {
            type: String,
            default: null,
        },
    },
    setup(props, { emit }) {
        const page = reactive({
            isLoading: false,
            output: false,
        });

        const { showToast } = useToast();
        const isMaterializing = ref(false);
        const selectedIds = ref([]);
        const selectAll = ref(false);
        const isAllSelected = ref(false);
        const showSelectMenu = ref(false);
        const dateStart = ref('');
        const dateEnd = ref('');

        const searchOptions = ref([
            { text: 'Member', value: 'member_name' },
            { text: 'Code', value: 'code' },
            { text: 'Date Range', value: 'date_range' },
        ]);

        const searchBy = reactive({
            selected: 'member_name',
            criteria: '',
        });

        const isSearchActive = ref(false);

        const searchPlaceholder = computed(() => {
            const option = searchOptions.value.find(o => o.value === searchBy.selected);
            return option ? `Search by ${option.text.toLowerCase()}...` : 'Search...';
        });

        const totalDataCount = computed(() => {
            return meta.value?.totalData?.total_data || pendingTransactions.value.length;
        });

        const fields = ref(['No', 'Code', 'Member', 'Point', 'Date', 'Action']);

        const meta = computed(() => store.state.PointHistory.meta);
        const pendingTransactions = computed(() => store.state.PointHistory.pendingTransactions);

        const fetchPending = async () => {
            page.isLoading = true;
            page.output = false;
            selectedIds.value = [];
            selectAll.value = false;
            isAllSelected.value = false;
            showSelectMenu.value = false;

            try {
                // Apply filter based on merchant or branch
                const filter = {};
                if (props.merchantId) {
                    filter.merchant_id = props.merchantId;
                } else if (props.branchId) {
                    filter.branch_id = props.branchId;
                }
                store.commit('PointHistory/add_filter', { filter });

                await store.dispatch('PointHistory/fetchPending');
            } catch (err) {
                console.error(err);
            } finally {
                page.isLoading = false;
                page.output = true;
            }
        };

        const selectSearchType = (value) => {
            searchBy.selected = value;
            searchBy.criteria = '';
            dateStart.value = '';
            dateEnd.value = '';
            isSearchActive.value = false;
        };

        const applyFilter = () => {
            const search = {};
            if (searchBy.selected !== 'date_range' && searchBy.criteria) {
                search[searchBy.selected] = searchBy.criteria;
            }
            store.commit('PointHistory/set_search', { search });

            const sort = {};
            if (searchBy.selected === 'date_range' && dateStart.value && dateEnd.value) {
                sort.created_at = `${dateStart.value},${dateEnd.value}`;
            }
            store.commit('PointHistory/set_sort', { sort });

            store.commit('PointHistory/reset_meta');
            isSearchActive.value = true;
            fetchPending();
        };

        const clearFilter = () => {
            searchBy.criteria = '';
            dateStart.value = '';
            dateEnd.value = '';
            isSearchActive.value = false;
            store.commit('PointHistory/clear_search');
            store.commit('PointHistory/clear_sort');
            store.commit('PointHistory/reset_meta');
            fetchPending();
        };

        const handleClickOutside = (e) => {
            if (!e.target.closest('.select-all-wrapper')) {
                showSelectMenu.value = false;
            }
        };

        onMounted(() => {
            fetchPending();
            document.addEventListener('click', handleClickOutside);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
            store.commit('PointHistory/clear_search');
            store.commit('PointHistory/clear_sort');
            store.commit('PointHistory/reset_meta');
        });

        const closeModal = () => {
            emit('close');
        };

        const handleMaterializeSingle = (id) => {
            isMaterializing.value = true;

            store.dispatch('PointHistory/releaseSingle', id)
                .then((response) => {
                    const data = response.data.data;
                    showToast({
                        type: 'success',
                        title: 'Success',
                        message: `Materialized transaction ${data.transaction_code}. ${data.point_awarded} point's awarded.`
                    });
                    emit('released');
                })
                .catch((err) => {
                    console.error('Error materializing transaction:', err);
                    showToast({
                        type: 'error',
                        title: 'Error',
                        message: err.response?.data?.message || 'Failed to materialize transaction'
                    });
                })
                .finally(() => {
                    isMaterializing.value = false;
                    fetchPending();
                });
        };

        const handleMaterializeBulk = () => {
            if (selectedIds.value.length === 0) return;

            isMaterializing.value = true;

            store.dispatch('PointHistory/releaseBulk', selectedIds.value)
                .then((response) => {
                    const data = response.data.data;
                    showToast({
                        type: 'success',
                        title: 'Success',
                        message: `Materialized ${data.total_released} transaction's. Total ${data.total_points_awarded} point's awarded.`
                    });
                    emit('released');
                })
                .catch((err) => {
                    console.error('Error materializing transactions:', err);
                    showToast({
                        type: 'error',
                        title: 'Error',
                        message: err.response?.data?.message || 'Failed to materialize transactions'
                    });
                })
                .finally(() => {
                    isMaterializing.value = false;
                    fetchPending();
                });
        };

        const toggleSelectAll = (mode) => {
            showSelectMenu.value = false;

            if (mode === 'all') {
                // Fetch all IDs from API
                store.dispatch('PointHistory/fetchAllPendingIds')
                    .then((allIds) => {
                        selectedIds.value = allIds;
                        selectAll.value = true;
                        isAllSelected.value = true;
                    })
                    .catch((err) => {
                        console.error('Failed to fetch all IDs:', err);
                    });
            } else if (mode === 'page') {
                selectedIds.value = pendingTransactions.value.map(item => item.id);
                selectAll.value = true;
                isAllSelected.value = false;
            } else {
                if (selectAll.value) {
                    store.dispatch('PointHistory/fetchAllPendingIds')
                        .then((allIds) => {
                            selectedIds.value = allIds;
                            isAllSelected.value = true;
                        })
                        .catch((err) => {
                            console.error('Failed to fetch all IDs:', err);
                        });
                } else {
                    selectedIds.value = [];
                    isAllSelected.value = false;
                }
            }
        };

        const clearSelection = () => {
            selectedIds.value = [];
            selectAll.value = false;
            isAllSelected.value = false;
            showSelectMenu.value = false;
        };

        const navPage = (pageNum) => {
            const _meta = { ...meta.value, page: pageNum };
            store.commit('PointHistory/switch_page', { meta: _meta });
            fetchPending();
        };

        const getMemberName = (item) => {
            if (item.member) {
                return `${item.member.title || ''} ${item.member.first_name || ''} ${item.member.last_name || ''}`.trim();
            }
            return '-';
        };

        const formatNumber = (value) => {
            if (value === null || value === undefined) return '0';
            return Number(value).toLocaleString('en-US');
        };

        const formatCurrency = (value) => {
            if (value === null || value === undefined) return '0';
            return Number(value).toLocaleString('en-US');
        };

        const formatDate = (dateStr) => {
            if (!dateStr) return '-';
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        };

        return {
            page,
            fields,
            meta,
            pendingTransactions,
            closeModal,
            handleMaterializeSingle,
            handleMaterializeBulk,
            toggleSelectAll,
            clearSelection,
            navPage,
            isMaterializing,
            selectedIds,
            selectAll,
            isAllSelected,
            showSelectMenu,
            totalDataCount,
            getMemberName,
            formatNumber,
            formatCurrency,
            formatDate,
            searchOptions,
            searchBy,
            isSearchActive,
            searchPlaceholder,
            selectSearchType,
            dateStart,
            dateEnd,
            applyFilter,
            clearFilter,
        };
    },
    components: {
        OModal,
        OTable,
        OPagination,
        DatePicker,
    },
};
</script>

<style scoped>
.pending-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    table-layout: fixed;
    width: 100%;
}

.pending-list-table th,
.pending-list-table td {
    padding: 8px 10px !important;
    vertical-align: middle;
    text-overflow: ellipsis;
    font-size: 0.8rem;
}

.member-row-clickable {
    transition: background-color 0.2s;
}

.member-row-clickable:hover {
    background-color: #f8fafc !important;
}

.code-badge {
    font-family: monospace;
    font-size: 0.75rem;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    color: #475569;
}

.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    border: none;
    cursor: pointer;
}

.btn-materialize {
    background-color: #ecfdf5;
    color: #065f46;
}

.btn-materialize:hover {
    background-color: #d1fae5;
    color: #064e3b;
}

.btn-materialize:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-materialize-bulk {
    background-color: var(--primary-color, #F93E3E);
    color: #fff;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    padding: 6px 16px;
    font-size: 13px;
    transition: all 0.2s ease;
}

.btn-materialize-bulk:hover {
    filter: brightness(0.9);
    color: var(--primary-color, #F93E3E);
    background-color: var(--secondary-color, #F93E3E);
}

.btn-materialize-bulk:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.primary-text {
    color: var(--primary-color, #F93E3E) !important;
}

/* Custom Search Box - matching Transaction.vue pattern */
.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color, #F93E3E);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary-color, #F93E3E), transparent 88%);
}

.form-control-search {
    border: none;
    padding: 8px 12px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
}

.form-control-search::placeholder {
    color: #94a3b8;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color, #F93E3E);
}

.date-input-group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
}

.form-control-date {
    border: none;
    outline: none;
    padding: 8px 8px;
    font-size: 14px;
    color: #334155;
    background: transparent;
    flex: 1;
    min-width: 0;
}

.date-separator {
    color: #94a3b8;
    font-weight: 500;
}

.form-check-input {
    cursor: pointer;
    width: 16px;
    height: 16px;
    border: 2px solid #cbd5e1;
    border-radius: 4px;
}

.form-check-input:checked {
    background-color: var(--primary-color, #F93E3E);
    border-color: var(--primary-color, #F93E3E);
}

.form-check-input:focus {
    border-color: var(--primary-color, #F93E3E);
    box-shadow: 0 0 0 0.2rem rgba(var(--primary-color, 249, 62, 62), 0.25);
}

/* Select All Dropdown */
.select-all-th {
    position: relative;
}

.select-all-wrapper {
    display: flex;
    align-items: center;
    gap: 2px;
}

.select-all-caret {
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    color: #94a3b8;
    border-radius: 4px;
    display: flex;
    align-items: center;
    transition: all 0.15s ease;
}

.select-all-caret:hover {
    color: #475569;
    background: #f1f5f9;
}

.select-all-menu {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 50;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    min-width: 220px;
    padding: 4px;
    margin-top: 4px;
}

.select-all-menu button {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: none;
    font-size: 0.8rem;
    color: #334155;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.15s ease;
    text-align: left;
}

.select-all-menu button:hover {
    background: #f1f5f9;
}

.select-all-menu button i {
    color: #64748b;
    width: 16px;
    text-align: center;
}

/* Selection Scope Badges */
.badge-all-selected,
.badge-page-selected {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 0.7rem;
    font-weight: 600;
    margin-left: 6px;
}

.badge-all-selected {
    background: color-mix(in srgb, var(--primary-color, #F93E3E), transparent 88%);
    color: var(--primary-color, #F93E3E);
}

.badge-page-selected {
    background: #f1f5f9;
    color: #64748b;
}
</style>

<!-- Unscoped: needed because OModal uses teleport to body -->
<style>
.omni-modal-container.modal-xl {
    min-width: 600px !important;
    max-width: 900px !important;
}
</style>
